import ToConnectionRequestTypeEnum from 'constants/enums/ToConnectionRequestTypeEnum';
import { ExperienceTypeEnum } from '@koob/enums';
import { getImageUrl } from '@koob/utils';
import { getHotel } from 'api/node/hotels';
import { getExperience } from 'api/node/experiences';
import { getTripTemplate } from 'api/node/trip';
import { parseBoolean } from 'utils';
import { dayjs } from '@koob/dates';
import { setIn } from 'formik';
import { allotmentLevelIndex } from '../../../utils/distributionInheritance';

// KD-3896: the margin type select's "Reset (inherit)" option, which clears
// the margin entered at that level.
export const MARGIN_RESET = 'reset';

const formatToconnectionRequestModelData = (values) => {
  const products = [];
  if (!values) return products;

  const formatPeriod = (period) => ({
    id: period.id,
    startAt: period.startAt,
    endAt: period.endAt,
    freeSale: period.freeSale ?? false,
    allotmentCount: period.totalAllotmentsAvailable ?? 0,
    // A margin of 0 is a value of its own, not an empty field.
    margin: parseMarginValue(period.margin) ?? undefined,
    // The selects write booleans while a reopened model injects 'true'/'false'
    // strings: a truthiness check would turn a boolean false (fixed amount)
    // into undefined, so normalize both forms. "Reset (inherit)" is a command
    // of its own, kept as is.
    isMarginPercent:
      period.isMarginPercent === MARGIN_RESET
        ? MARGIN_RESET
        : parseBoolean(period.isMarginPercent),
  });

  const formatRoom = (room) => ({
    id: room.id,
    enabled: room.enabled === 'true' || room.enabled === true,
    periods: room.periods?.map(formatPeriod) || [],
  });

  const addProduct = (item, type) => {
    const isSelected = item.isSelected !== false && item.isSelected !== 'false';
    const isEnabled =
      isSelected && item.distribute !== 'false' && item.distribute !== false;

    // Same boolean/string duality as periods: a boolean false (fixed amount)
    // must not fall back to the percent default.
    const isMarginPercent = parseBoolean(item.isMarginPercent) ?? true;

    const product = {
      productId: item.id,
      type,
      margin: item.margin ? Number(item.margin) : undefined,
      isMarginPercent,
      enabled: isEnabled,
      name: item.name,
      image: item.image,
      description: item.description,
      stars: item.stars,
      state: item.state,
    };

    if (type === ToConnectionRequestTypeEnum.HOTEL && item.rooms) {
      product.rooms = item.rooms.map(formatRoom);
    }
    // KD-3896: the room and room × season margin commands travel with the
    // model, the periods cannot hold them ("Apply" clears their margins).
    if (
      type === ToConnectionRequestTypeEnum.HOTEL &&
      item.distributionRooms?.length
    ) {
      product.distributionRooms = item.distributionRooms;
    }

    products.push(product);
  };

  const addProducts = (items, type) => {
    if (!items) return;

    for (const item of items) {
      const isSelected =
        item.isSelected !== false && item.isSelected !== 'false';
      if (!isSelected && !item.fromModel) continue;
      addProduct(item, type);
    }
  };

  addProducts(
    values.toConnectionRequestHotels,
    ToConnectionRequestTypeEnum.HOTEL,
  );
  addProducts(
    values.toConnectionRequestExperiences,
    ToConnectionRequestTypeEnum.EXPERIENCE,
  );
  addProducts(
    values.toConnectionRequestTemplates,
    ToConnectionRequestTypeEnum.TEMPLATE,
  );

  return products;
};

export const getProductDetails = async (product) => {
  switch (product.type) {
    case ToConnectionRequestTypeEnum.HOTEL: {
      const response = await getHotel({ hotelId: product.productId });
      const hotel = response?.data;
      return {
        image: getImageUrl(hotel),
        name: hotel?.displayName,
        description: hotel?.address,
        stars: hotel?.stars,
      };
    }
    case ToConnectionRequestTypeEnum.EXPERIENCE: {
      const response = await getExperience({ experienceId: product.productId });
      const experience = response?.data;
      return {
        image: getImageUrl(experience),
        name: experience?.name,
        description: experience?.isExtra
          ? ExperienceTypeEnum.EXTRA
          : experience?.experienceType,
      };
    }
    case ToConnectionRequestTypeEnum.TEMPLATE: {
      const response = await getTripTemplate({ templateId: product.productId });
      const template = response?.data;
      return {
        image: template?.imageUrl,
        name: template?.name,
        description: template?.description,
      };
    }
    default:
      return {};
  }
};

export const formatModelToFormikData = ({ model }) => {
  const formikData = {
    toConnectionRequestHotels: [],
    toConnectionRequestExperiences: [],
    toConnectionRequestTemplates: [],
  };

  if (!model?.products) return formikData;

  const formatMarginPercentToFormik = (value) => {
    const parsed = parseBoolean(value);
    return parsed !== undefined ? String(parsed) : undefined;
  };

  const formatPeriodToFormik = (period) => ({
    id: period.id,
    startAt: period.startAt,
    endAt: period.endAt,
    freeSale: period.freeSale ?? false,
    totalAllotmentsAvailable: period.allotmentCount ?? 0,
    margin: period.margin?.toString() || '',
    isMarginPercent:
      period.isMarginPercent === MARGIN_RESET
        ? MARGIN_RESET
        : formatMarginPercentToFormik(period.isMarginPercent),
  });

  const formatRoomToFormik = (room) => ({
    id: room.id,
    enabled: room.enabled ? 'true' : 'false',
    periods: room.periods?.map(formatPeriodToFormik) || [],
  });

  model.products.forEach((product) => {
    const baseProduct = {
      id: product.productId,
      margin: product.margin,
      isMarginPercent: formatMarginPercentToFormik(product.isMarginPercent),
      distribute: product.enabled !== false ? 'true' : 'false',
      isSelected: product.enabled !== false,
      fromModel: true,
      name: product.name,
      image: product.image,
      description: product.description,
      stars: product.stars,
      state: product.state,
    };

    if (product.type === ToConnectionRequestTypeEnum.HOTEL) {
      formikData.toConnectionRequestHotels.push({
        ...baseProduct,
        rooms: product.rooms?.map(formatRoomToFormik) || [],
        distributionRooms: product.distributionRooms ?? [],
      });
    }

    if (product.type === ToConnectionRequestTypeEnum.EXPERIENCE) {
      formikData.toConnectionRequestExperiences.push(baseProduct);
    }

    if (product.type === ToConnectionRequestTypeEnum.TEMPLATE) {
      formikData.toConnectionRequestTemplates.push(baseProduct);
    }
  });

  return formikData;
};

// The bulk-edit selects use boolean option values ('' = no change) while the
// formik state holds 'true'/'false' strings when it comes from a model jsonb
// (KD-3355): without normalization every select displays "No change" when a
// model is reopened.
export const toBooleanSelectValue = (value) => parseBoolean(value) ?? '';

// Shared margin normalization for the bulk-edit payloads: empty and
// non-numeric inputs become null instead of leaking NaN into the request.
export const parseMarginValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

// A level's margin command: "No change" and a type without a value send
// nothing, "Reset (inherit)" clears the level's margin, a type sends the margin.
const marginCommand = (level) => {
  if (level.isMarginPercent === MARGIN_RESET) {
    return { margin: null, isMarginPercent: null };
  }
  const isMarginPercent = parseBoolean(level.isMarginPercent);
  const margin = parseMarginValue(level.margin);
  if (isMarginPercent === undefined || margin === null) {
    return {};
  }
  return { isMarginPercent, margin };
};

// KD-3622: distribution models freeze period ids at save time while contract
// publication recreates the periods (same room, same dates, new ids). The
// payload carries roomId + startAt/endAt so the API can fall back to a
// (room, dates) match when the id is stale.
export const buildHotelPeriodsPayload = (hotel) => {
  return (hotel.rooms ?? [])
    .map((room) => {
      if (parseBoolean(room.enabled) === undefined) {
        return null;
      }

      return room.periods?.map((period) => ({
        id: `${period.id}`,
        roomId: room.id !== undefined ? `${room.id}` : undefined,
        startAt: period.startAt || undefined,
        endAt: period.endAt || undefined,
        isPeriodAllowedForBooking: parseBoolean(room.enabled),
        ...marginCommand(period),
      }));
    })
    .flat()
    .filter(Boolean);
};

// KD-3896: the room and room × season margin commands. `overrideChildren`
// ("Apply to all…") also clears the margins entered below the level; an apply
// left without its value clears nothing.
export const buildHotelRoomsPayload = (hotel) => {
  const level = (values) => {
    const command = marginCommand(values);
    return {
      id: `${values.id}`,
      ...command,
      ...(values.overrideChildren && 'margin' in command
        ? { overrideChildren: true }
        : {}),
    };
  };
  const isCommand = (values) => 'margin' in values || values.overrideChildren;

  return (hotel.distributionRooms ?? [])
    .map((room) => ({
      ...level(room),
      seasons: (room.seasons ?? []).map(level).filter(isCommand),
    }))
    .filter((room) => isCommand(room) || room.seasons.length > 0);
};

// The allotment a period ends up with must fit what is left to distribute on
// it, the periods not shown included: the error goes on the level the
// allotment was entered at. A hidden room holds none.
export const validateRoomsAllotment = (
  values,
  { canDisplayPeriod, canDisplaySeason, showsRoomLevel, message },
) => {
  let errors = {};
  values.rooms.forEach((room, roomIndex) => {
    if (!parseBoolean(room.enabled)) return;
    room.periods.forEach((period, periodIndex) => {
      const seasonIndex = room.seasons.findIndex(
        ({ id }) => id === period.seasonRoomId,
      );
      const season = room.seasons[seasonIndex];
      const levels = [
        {
          name: `rooms.${roomIndex}.periods.${periodIndex}.own`,
          values: period.own,
          shown: canDisplayPeriod(period),
        },
        {
          name: `rooms.${roomIndex}.seasons.${seasonIndex}.distribution`,
          values: season?.distribution,
          shown: season?.offered && canDisplaySeason(season),
        },
        {
          name: `rooms.${roomIndex}.distribution`,
          values: room.distribution,
          shown: showsRoomLevel(room),
        },
      ];
      const level =
        levels[allotmentLevelIndex(levels.map(({ values }) => values))];
      if (
        level?.shown &&
        Number(level.values.allotmentCount) >
          period.toElement.totalAllotmentsAvailableForDistribution
      ) {
        errors = setIn(errors, `${level.name}.allotmentCount`, message);
      }
    });
  });
  return errors;
};

// KD-3622: the rooms modal displays the CURRENT contract periods while the
// formik values come from the model — realign the model periods onto the
// current ones (by id, then by dates) so the margins shown and edited are
// bound to periods that actually exist.
export const alignModelRoomsToCurrentPeriods = (modelRooms, currentRooms) => {
  if (!modelRooms?.length || !currentRooms?.length) {
    return modelRooms;
  }

  // Robust to both ISO strings (model jsonb, API responses) and Date objects.
  const sameDay = (a, b) =>
    Boolean(a) && Boolean(b) && dayjs(a).isSame(dayjs(b), 'day');

  return modelRooms.map((modelRoom) => {
    const currentRoom = currentRooms.find(
      (room) => String(room.id) === String(modelRoom?.id),
    );
    if (!currentRoom || !modelRoom?.periods) {
      return modelRoom;
    }

    return {
      ...modelRoom,
      periods: currentRoom.periods.map((current) => {
        const fromModel =
          modelRoom.periods.find(
            (period) => String(period?.id) === String(current.id),
          ) ??
          modelRoom.periods.find(
            (period) =>
              sameDay(period?.startAt, current.startAt) &&
              sameDay(period?.endAt, current.endAt),
          );

        return {
          ...(fromModel ?? {}),
          id: `${current.id}`,
          startAt: current.startAt,
          endAt: current.endAt,
        };
      }),
    };
  });
};

// KD-3712: when applying a hotel distribution model, any room of the hotel
// that was not configured in the model must become invisible on the TOs.
// This helper preserves configured model rooms (realigned to current periods)
// and appends all missing contract rooms with enabled: 'false' so the API
// explicitly sets isPeriodAllowedForBooking to false.
export const completeModelRoomsWithMissing = (modelRooms, currentRooms) => {
  if (!currentRooms?.length) {
    return modelRooms ?? [];
  }

  const configuredModelRooms = (modelRooms ?? []).filter(
    (room) => parseBoolean(room?.enabled) !== undefined,
  );

  const alignedModelRooms = alignModelRoomsToCurrentPeriods(
    configuredModelRooms,
    currentRooms,
  );

  const missingRooms = currentRooms.reduce((acc, current) => {
    const isConfigured = configuredModelRooms.some(
      (mr) => String(mr?.id) === String(current.id),
    );

    if (!isConfigured) {
      acc.push({
        id: `${current.id}`,
        enabled: 'false',
        periods: (current.periods ?? []).map((period) => ({
          id: `${period.id}`,
          startAt: period.startAt,
          endAt: period.endAt,
        })),
      });
    }

    return acc;
  }, []);

  return [...alignedModelRooms, ...missingRooms];
};

export default formatToconnectionRequestModelData;
